import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Employee,
  PersonaExpanded,
  getDefaultUserAvatar,
  EmployeeRecord,
} from "winslow-core";
import TextInput from "./TextInput";
import { ValidatedImage } from "./ValidatedImage";
import "./CirclePersonaEmployeeSelect.css";
import {
  addRecentSelection,
  selectRecentSelections,
  selectEmployeeRecords,
} from "../../redux/features/agent/agentSlice";
import { selectUser } from "../../redux/features/appData";
import { selectHasNativeHRIS } from "../../redux/features/appData";
import { erGetCPValue } from "../app/app-employee-records/employeeRecordUtils";

type CirclePersonaEmployeeSelectProps = {
  onChange: (value: string, isPersona: boolean) => void;
  includeNoFilter?: boolean;
  selectedEmployee?: string;
  selectedPersona?: string;

  presetEmployeeId?: string;
  employees?: Employee[];
  personas?: PersonaExpanded[];
  disabled?: boolean;
};

export const CirclePersonaEmployeeSelect = ({
  onChange,
  selectedEmployee,
  selectedPersona,

  includeNoFilter = false,
  presetEmployeeId,
  employees,
  personas,
  disabled = false,
}: CirclePersonaEmployeeSelectProps) => {
  const dispatch = useDispatch();
  const recentSelections = useSelector(selectRecentSelections);
  const storeUser = useSelector(selectUser);
  const currentWorkspaceId = storeUser?.currentWorkspaceId;
  const hasNativeHRIS = useSelector(selectHasNativeHRIS);
  const employeeRecordsFromRedux = useSelector(selectEmployeeRecords);

  // Use EmployeeRecords when hasNativeHRIS is true, otherwise use Employees
  const displayData = hasNativeHRIS ? employeeRecordsFromRedux : employees;

  const [isOpen, setIsOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
    imageUrl?: string;
    isPersona: boolean;
  } | null>(null);

  const selectorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Helper function to get display name from Employee or EmployeeRecord
  const getDisplayName = (item: Employee | EmployeeRecord): string => {
    if (hasNativeHRIS && "workEmail" in item) {
      // EmployeeRecord
      const firstName = erGetCPValue(item, "system-firstName");
      const lastName = erGetCPValue(item, "system-lastName");
      return (
        [firstName, lastName].filter(Boolean).join(" ").trim() ||
        item.workEmail ||
        "Unknown"
      );
    } else {
      // Employee
      const emp = item as Employee;
      return emp.displayName || emp.email || "Unknown Employee";
    }
  };

  // Helper function to get avatar from Employee or EmployeeRecord
  const getAvatar = (item: Employee | EmployeeRecord): string => {
    if (hasNativeHRIS && "workEmail" in item) {
      // EmployeeRecord
      const displayName = getDisplayName(item);
      return item.avatarURL || getDefaultUserAvatar(displayName);
    } else {
      // Employee
      const emp = item as Employee;
      const displayName = emp.displayName || emp.email || "";
      return (
        emp?.profile?.avatar ||
        emp?.avatar ||
        emp?.data?.avatar ||
        getDefaultUserAvatar(displayName)
      );
    }
  };

  // Add effect to handle presetEmployeeId
  useEffect(() => {
    if (presetEmployeeId && displayData && displayData.length > 0) {
      const item = displayData.find((e) => e.id === presetEmployeeId);
      if (item) {
        onChange(presetEmployeeId, false);
        setSelectedOption({
          value: item.id,
          label: getDisplayName(item),
          imageUrl: getAvatar(item),
          isPersona: false,
        });
      }
    }
  }, [presetEmployeeId, displayData, onChange, hasNativeHRIS]);

  // Initial selected value setup
  useEffect(() => {
    if (selectedEmployee && displayData && displayData.length > 0) {
      const item = displayData.find((e) => e.id === selectedEmployee);
      if (item) {
        setSelectedOption({
          value: item.id,
          label: getDisplayName(item),
          imageUrl: getAvatar(item),
          isPersona: false,
        });
      }
    } else if (selectedPersona && personas && personas.length > 0) {
      const persona = personas.find((p) => p.id === selectedPersona);
      if (persona) {
        setSelectedOption({
          value: persona.id,
          label: persona.name || "Unknown Persona",
          imageUrl: "/icons/light-mode/persona_manager_off.svg",
          isPersona: true,
        });
      }
    } else {
      setSelectedOption(null);
    }
  }, [selectedEmployee, selectedPersona, displayData, personas, hasNativeHRIS]);

  // Clear orphaned selections (when selected ID doesn't exist in current workspace)
  useEffect(() => {
    if (selectedEmployee && displayData && displayData.length > 0) {
      const item = displayData.find((e) => e.id === selectedEmployee);
      if (!item) {
        // Employee/EmployeeRecord doesn't exist in current workspace - clear selection
        onChange("", false);
      }
    } else if (selectedPersona && personas && personas.length > 0) {
      const persona = personas.find((p) => p.id === selectedPersona);
      if (!persona) {
        // Persona doesn't exist in current workspace - clear selection
        onChange("", false);
      }
    }
  }, [selectedEmployee, selectedPersona, displayData, personas, onChange]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        selectorRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Convert employees/employee records to option format and filter by search term
  const employeeOptions =
    displayData && searchTerm.trim()
      ? displayData
          .map((item) => {
            const label = getDisplayName(item);
            const imageUrl = getAvatar(item);

            return {
              value: item.id,
              label,
              imageUrl,
              fallbackAvatar: getDefaultUserAvatar(label),
              isPersona: false,
            };
          })
          .filter(
            (option) =>
              option.label &&
              option.label.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
          .slice(0, 5)
      : [];

  // Map personas to options and filter by search term
  const personaOptions =
    personas && searchTerm.trim()
      ? personas
          .map((persona) => ({
            value: persona.id,
            label: persona.name || "Unknown Persona",
            imageUrl: "/icons/light-mode/persona_manager_off.svg",
            isPersona: true,
          }))
          .filter(
            (option) =>
              option.label &&
              option.label.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
          .slice(0, 5)
      : [];

  // Get recent selections - filter by workspace and validate against current employees/personas
  // Only show recent selections that exist in the current workspace
  const recentOptions =
    !searchTerm.trim() && recentSelections && recentSelections.length > 0
      ? recentSelections.filter((selection) => {
          // Validate that the selection still exists in current workspace
          if (selection.isPersona) {
            return personas?.some((p) => p.id === selection.value);
          } else {
            return employees?.some((e) => e.id === selection.value);
          }
        })
      : [];

  const handleToggleMenu = () => {
    // Allow opening even when disabled/HRIS not connected to enable persona selection
    setIsOpen(!isOpen);
  };

  const handleSelect = (option: typeof selectedOption) => {
    if (option === null) {
      onChange("", false);
      setSelectedOption(null);
    } else {
      onChange(option.value, option.isPersona);
      setSelectedOption(option);

      // Add to recent selections with workspaceId
      if (currentWorkspaceId) {
        console.log(
          "[CirclePersonaEmployeeSelect] Dispatching addRecentSelection"
        );
        dispatch(
          addRecentSelection({
            value: option.value,
            label: option.label,
            imageUrl: option.imageUrl,
            isPersona: option.isPersona,
            workspaceId: currentWorkspaceId,
          })
        );
      }
    }
    setIsOpen(false);
    setSearchTerm(""); // Clear search when selection is made
  };

  // Helper function to render an option with selected styling
  const renderOption = (
    option: typeof selectedOption,
    isSelected: boolean = false
  ) => (
    <div
      key={option?.value || "no-filter"}
      className={`option ${isSelected ? "selected" : ""}`}
      onClick={() => handleSelect(option)}>
      {option ? (
        <>
          <ValidatedImage
            imageUrl={option.imageUrl || ""}
            fallbackUrl={
              option.isPersona
                ? "/icons/light-mode/persona_manager_off.svg"
                : getDefaultUserAvatar(option.label)
            }
            alt={option.label}
            className="option-avatar"
          />
          <span>{option.label}</span>
        </>
      ) : (
        <span>No context</span>
      )}
    </div>
  );

  return (
    <>
      <div className="circle-employee-selector" ref={selectorRef}>
        <div
          className={`circle-selector ${selectedOption ? "selected" : "empty"}`}
          onClick={handleToggleMenu}>
          {selectedOption ? (
            <>
              <ValidatedImage
                imageUrl={selectedOption.imageUrl || ""}
                fallbackUrl={getDefaultUserAvatar(selectedOption.label)}
                alt={selectedOption.label}
                className="circle-avatar"
              />
              <span className="selected-label">{selectedOption.label}</span>
              <div
                id="clear-persona-button"
                className="clear-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(null);
                }}>
                ×
              </div>
            </>
          ) : (
            <div className="placeholder-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                {/* <circle
                  cx="12"
                  cy="12"
                  r="12"
                  fill="#FBFCFD"
                  stroke="#EBECF0"
                  strokeWidth="0.8"
                /> */}
                <path
                  d="M10.9999 14.3333H9.99992C9.06954 14.3333 8.60435 14.3333 8.22582 14.4482C7.37356 14.7067 6.70661 15.3736 6.44808 16.2259C6.33325 16.6044 6.33325 17.0696 6.33325 18M14.6666 9C14.6666 10.6569 13.3234 12 11.6666 12C10.0097 12 8.66659 10.6569 8.66659 9C8.66659 7.34315 10.0097 6 11.6666 6C13.3234 6 14.6666 7.34315 14.6666 9ZM12.3333 18L14.4008 17.4093C14.4998 17.381 14.5493 17.3668 14.5955 17.3456C14.6365 17.3268 14.6755 17.3039 14.7119 17.2772C14.7528 17.2471 14.7892 17.2107 14.862 17.1379L19.1666 12.8334C19.6269 12.3731 19.6269 11.6269 19.1666 11.1666C18.7064 10.7064 17.9602 10.7064 17.4999 11.1667L13.1954 15.4712C13.1226 15.544 13.0861 15.5804 13.0561 15.6214C13.0294 15.6578 13.0064 15.6967 12.9876 15.7377C12.9664 15.7839 12.9523 15.8334 12.924 15.9324L12.3333 18Z"
                  stroke="#FF0909"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>

        {isOpen && (
          <div className="circle-dropdown-menu" ref={menuRef}>
            {/* Search input at the top */}
            <div
              className="search-container"
              style={{ padding: "8px", borderBottom: "1px solid #EBECF0" }}>
              <TextInput
                title="Search"
                value={searchTerm}
                setValue={setSearchTerm}
                placeholder="Type an employee name or persona..."
                disabled={false}
              />
            </div>

            {/* Show no context option */}
            {includeNoFilter && !searchTerm.trim() && (
              <div className="menu-section">{renderOption(null)}</div>
            )}

            {/* Show recent selections when no search term */}
            {!searchTerm.trim() && recentOptions.length > 0 && (
              <div className="menu-section">
                <div className="section-label">Recently Selected</div>
                {recentOptions.map((option) =>
                  renderOption(option, option.value === selectedOption?.value)
                )}
              </div>
            )}

            {/* Show search results when there's a search term */}
            {searchTerm.trim() && (
              <>
                {employeeOptions.length > 0 && (
                  <div className="menu-section">
                    <div className="section-label">Employees</div>
                    {employeeOptions.map((option) => renderOption(option))}
                  </div>
                )}

                {personaOptions.length > 0 && (
                  <div className="menu-section">
                    <div className="section-label">Personas</div>
                    {personaOptions.map((option) => renderOption(option))}
                  </div>
                )}

                {/* Show message when no results found */}
                {employeeOptions.length === 0 &&
                  personaOptions.length === 0 && (
                    <div className="menu-section">
                      <div
                        className="option"
                        style={{ color: "#666", fontStyle: "italic" }}>
                        No results found for "{searchTerm}"
                      </div>
                    </div>
                  )}
              </>
            )}

            {/* Show helpful message when no search and no recents */}
            {!searchTerm.trim() &&
              recentOptions.length === 0 &&
              !includeNoFilter && (
                <div className="menu-section">
                  <div
                    className="option"
                    style={{ color: "#666", fontStyle: "italic" }}>
                    Start typing to search...
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </>
  );
};

export default CirclePersonaEmployeeSelect;
